import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import ImageRounded from "@mui/icons-material/ImageRounded";

/**
 * Preset file kinds for {@link FileDropAssetField} (accept + copy + chip).
 * @enum {string}
 */
export const AssetType = Object.freeze({
  PDF: "pdf",
  IMAGE: "image",
});

function defaultFormatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return `${size.toFixed(size >= 100 ? 0 : size >= 10 ? 1 : 2)} ${units[i]}`;
}

/**
 * UI + accept metadata per {@link AssetType}.
 * @type {Record<string, {
 *   accept: string,
 *   dragLine: string,
 *   hintPrefix: string,
 *   statusChipText: string,
 *   StatusChipIcon: import('react').ElementType,
 * }>}
 */
export const ASSET_TYPE_CONFIG = {
  [AssetType.PDF]: {
    accept: "application/pdf,.pdf",
    dragLine: "Arraste e solte o PDF aqui, ou",
    hintPrefix: "Apenas PDF",
    statusChipText: "Arquivo .pdf",
    StatusChipIcon: PictureAsPdfRoundedIcon,
  },
  [AssetType.IMAGE]: {
    accept:
      "image/png,image/jpeg,image/jpg,image/heic,image/heif,.png,.jpg,.jpeg,.heic,.heif",
    dragLine: "Arraste e solte a imagem aqui, ou",
    hintPrefix: "Apenas PNG, JPEG, HEIC ou HEIF",
    statusChipText: "Imagem",
    StatusChipIcon: ImageRounded,
  },
};

/**
 * @param {string} assetType
 * @returns {string}
 */
export function getAssetAccept(assetType) {
  const cfg = ASSET_TYPE_CONFIG[assetType];
  return cfg ? cfg.accept : ASSET_TYPE_CONFIG[AssetType.PDF].accept;
}

/**
 * @param {string} assetType
 * @param {number} maxBytes
 * @param {(n: number) => string} [formatBytes]
 * @returns {string}
 */
export function buildAssetHint(assetType, maxBytes, formatBytes = defaultFormatBytes) {
  const cfg = ASSET_TYPE_CONFIG[assetType] ?? ASSET_TYPE_CONFIG[AssetType.PDF];
  return `${cfg.hintPrefix} • até ${formatBytes(maxBytes)}`;
}

const IMAGE_MIME = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/heic",
  "image/heif",
]);

const IMAGE_EXT = [".png", ".jpg", ".jpeg", ".heic", ".heif"];

function isPdfFile(file) {
  if (!file) return false;
  if (file.type === "application/pdf") return true;
  const name = file.name || "";
  return typeof name === "string" && name.toLowerCase().endsWith(".pdf");
}

function isImageFile(file) {
  if (!file) return false;
  const type = (file.type || "").toLowerCase();
  if (type && IMAGE_MIME.has(type)) return true;
  const name = (file.name || "").toLowerCase();
  return IMAGE_EXT.some((ext) => name.endsWith(ext));
}

/**
 * @param {File} file
 * @param {string} assetType
 * @param {number} maxBytes
 * @param {(n: number) => string} [formatBytes]
 * @returns {{ valid: true } | { valid: false, message: string }}
 */
export function validateAssetFile(file, assetType, maxBytes, formatBytes = defaultFormatBytes) {
  if (!file) {
    return { valid: false, message: "Nenhum arquivo selecionado." };
  }
  if (file.size > maxBytes) {
    return {
      valid: false,
      message: `O arquivo excede o limite de ${formatBytes(maxBytes)}.`,
    };
  }
  const at = assetType ?? AssetType.PDF;
  if (at === AssetType.PDF) {
    if (!isPdfFile(file)) {
      return { valid: false, message: "Apenas arquivos PDF são permitidos." };
    }
  } else if (at === AssetType.IMAGE) {
    if (!isImageFile(file)) {
      return {
        valid: false,
        message: "Apenas imagens PNG, JPEG, HEIC ou HEIF são permitidas.",
      };
    }
  }
  return { valid: true };
}
